import React, {useEffect, useRef, useState} from 'react';
import LoadingIndicator from "../../pages/LoadingIndicator";
import {reportingApiClient} from "../ReportingApiClient";
import {RouteWithId} from "../../routes/types";
import {useParams} from "react-router-dom";
import {Document, Page, pdfjs} from "react-pdf";

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import {AdvisorDocumentMap, GenerateReportRequest, ReportConfig} from "../models/Reporting";

import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {
    selectIsExtendedFamilySelected,
    selectIsOtherMembersSelected
} from "../../ClientManagement/FamilyTree/FamilyTreeSlice";
import {
    selectAdviceLibraryDocuments,
    selectIsReportGenerationTriggered,
    selectOptionsForGoalFundingReport,
    selectOptionsForNWOTReport,
    selectPracticeDocuments,
    selectSelectedClientDocuments,
    selectSelectedMyDocuments,
    selectSelectedPagesForReport,
    setIsReportGenerationTriggered
} from "./ReportingSlice";
import usePageViewTimer from "../../hooks/usePageViewTimer";
import {selectAdviceLibrarySubMenus, selectPracticeDocumentsSubMenus} from "../../Resources/menuStructureSlice";
import ReportingConnectionFailedPage from './ReportingConnectionFailedPage';
import {DCHToasts, MessageFromDCH} from "./DCHToasts";
import {
    selectAssetReliancePageActionMenuForDetailedReport,
    selectAssetReliancePageActionMenuForSummaryReport
} from "../../ClientManagement/AssetReliance/AssetRelianceSlice";
import {selectComparePlanPageActionMenuForReport} from "../../Plans/CompareScenarios/ComparePlansSlice";
import {useRelayContext} from "../../ClientManagement/Meeting/Relay/types/RelayContext";
import {setHasCanvasRendered} from "../../ClientManagement/Meeting/meetingSlice";
import {App_Conf} from "../../core/app_conf";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type PreviewReportProps = {
    displayName: string,
    isGenerating: boolean,
    isUploading: boolean,
    setIsGenerating: (value: boolean) => void,
    generatedReportUrl: string,
    setGeneratedReportUrl: (url: string) => void,
    showMessageFromDCH?: boolean,
    messageFromDCH?: MessageFromDCH
    onCloseSuccessMessage?: () => void,
    onCloseFailureMessage?: () => void,
    showFailureMessageFromWDS?: boolean,
    failureMessageFromWDS?: string,
    hasError: boolean,
    setHasError: (value: boolean) => void
}

const PreviewReport = ({
                           displayName,
                           isGenerating,
                           isUploading,
                           setIsGenerating,
                           generatedReportUrl,
                           setGeneratedReportUrl,
                           showMessageFromDCH = false,
                           messageFromDCH = {
                               title: "",
                               message: "",
                               type: ""
                           },
                           onCloseSuccessMessage,
                           onCloseFailureMessage,
                           showFailureMessageFromWDS = false,
                           failureMessageFromWDS = "",
                           hasError,
                           setHasError
                       }: PreviewReportProps) => {
    const {id} = useParams<RouteWithId>();
    const [noOfPages, setNoOfPages] = useState(0);
    const [reportStatus, setReportStatus] = useState<string>("");

    const nwotOptions = useAppSelector(selectOptionsForNWOTReport);
    const goalOptions = useAppSelector(selectOptionsForGoalFundingReport);
    const isOtherMembersSelected = useAppSelector(selectIsOtherMembersSelected);
    const isExtendedFamilySelected = useAppSelector(selectIsExtendedFamilySelected);
    const selectedPagesForAnnualReportFromRedux = useAppSelector(selectSelectedPagesForReport);
    const selectedClientDocuments = useAppSelector(selectSelectedClientDocuments);
    const selectedMyDocuments = useAppSelector(selectSelectedMyDocuments);
    const selectedAdviceLibraryDocuments = useAppSelector(selectAdviceLibraryDocuments);
    const selectedPracticeDocuments = useAppSelector(selectPracticeDocuments);
    const reportConfig: ReportConfig = JSON.parse(JSON.stringify(selectedPagesForAnnualReportFromRedux));
    const pagesList = [...Array(noOfPages)];
    const adviceLibrarySubMenu = useAppSelector(selectAdviceLibrarySubMenus);
    const practiceDocumentSubMenu = useAppSelector(selectPracticeDocumentsSubMenus);
    const assetRelianceButtonStateForSummaryReport = useAppSelector(selectAssetReliancePageActionMenuForSummaryReport);
    const assetRelianceButtonStateForDetailedReport = useAppSelector(selectAssetReliancePageActionMenuForDetailedReport);
    const comparePlanButtonStateForReport = useAppSelector(selectComparePlanPageActionMenuForReport);
    const dispatch = useAppDispatch();
    const isReportGenerationTriggered = useAppSelector(selectIsReportGenerationTriggered);
    const statusIntervalRef = useRef<ReturnType<typeof setInterval>>();
    const statusCountRef = useRef<number>(0);

    const relayContext = useRelayContext();

    usePageViewTimer('Generate Report Page Load Timer (milliseconds)', isGenerating);

    useEffect(() => {
        setIsGenerating(true);

        return () => {
            if (relayContext) {
                relayContext.cleanUpSharedObjects();
            }
            dispatch(setHasCanvasRendered(false));
            URL.revokeObjectURL(generatedReportUrl);
        }
    }, []);

    useEffect(() => {
        if (reportStatus && reportStatus === "COMPLETED") {
            reportingApiClient.getGeneratedReport(id).then((blobPdf) => {
                setHasError(false);
                setIsGenerating(false);
                if (blobPdf.size) {
                    const blobUrl = URL.createObjectURL(blobPdf);
                    setGeneratedReportUrl(blobUrl);
                    dispatch(setIsReportGenerationTriggered(false));
                }
            }).catch((_error) => {
                if (_error.stack === "TypeError: Failed to fetch") {
                    resetReportStatusToInProgress()
                } else {
                    resetReportStates();
                }
            });
        }

        if ((reportStatus && reportStatus === "IN PROGRESS") || isReportGenerationTriggered === true) {
            statusIntervalRef.current = setInterval(() => {
                reportingApiClient.getReportingStatus(id).then((reportStatusResponse) => {
                    ++statusCountRef.current;
                    if (statusCountRef.current === 300) {
                        clearInterval(statusIntervalRef.current);
                        resetReportStates();
                    }
                    if (reportStatusResponse.status === "COMPLETED") {
                        clearInterval(statusIntervalRef.current);
                        setReportStatus(reportStatusResponse.status);
                    }
                    if (reportStatusResponse.status === "ERROR") {
                        resetReportStates();
                        clearInterval(statusIntervalRef.current);
                    }
                }).catch(() => {
                    resetReportStates();
                    clearInterval(statusIntervalRef.current);
                });
            }, 3000);
        }

        return () => statusIntervalRef.current && clearInterval(statusIntervalRef.current);
    }, [reportStatus]);

    useEffect(() => {
        if (isGenerating && !reportStatus && isReportGenerationTriggered === false) {
            generateClientReport();
        }

    }, [isGenerating]);

    function resetReportStates() {
        statusCountRef.current = 0;
        setReportStatus("");
        setIsGenerating(false);
        setHasError(true);
        dispatch(setIsReportGenerationTriggered(false));
    }

    function resetReportStatusToInProgress() {
        statusCountRef.current = 0;
        setReportStatus("IN PROGRESS");
    }

    const generateClientReport = () => {
        URL.revokeObjectURL(generatedReportUrl);

        if (reportConfig.familyTree !== null && reportConfig.familyTree.isEnabled) {
            reportConfig.familyTree.familyTreeScreenShotDetails = {
                isOtherMembersSelected: isOtherMembersSelected,
                isExtendedFamilySelected: isExtendedFamilySelected
            };
        }
        if (reportConfig.netWorthOverTime !== null && reportConfig.netWorthOverTime.isEnabled) {
            reportConfig.netWorthOverTime.netWorthOverTimeOptions = {
                discountRate: nwotOptions.discountRate,
                insuranceIncluded: nwotOptions.insuranceIncluded
            }
        }

        if (reportConfig.goalFunding !== null && reportConfig.goalFunding.isEnabled) {
            reportConfig.goalFunding.goalFundingOptions = {
                displayView: goalOptions.displayView,
                displayOptions: goalOptions.displayOptions,
                fundingAge: goalOptions.fundingAge
            }
        }

        if (reportConfig.assetRelianceWithFutureValue !== null && reportConfig.assetRelianceWithFutureValue.isEnabled) {
            reportConfig.assetRelianceWithFutureValue.assetRelianceOptions = {
                showExpectedExcessAsset: assetRelianceButtonStateForSummaryReport.showExpectedExcessAsset,
                includeLifeInsuranceAtDeath: assetRelianceButtonStateForSummaryReport.includeLifeInsuranceAtDeath,
                selectedFveDiscountRate: assetRelianceButtonStateForSummaryReport.selectedFveDiscountRate
            }
        }

        if (reportConfig.assetRelianceDetailedWithFutureValue !== null && reportConfig.assetRelianceDetailedWithFutureValue.isEnabled) {
            reportConfig.assetRelianceDetailedWithFutureValue.assetRelianceOptions = {
                showExpectedExcessAsset: assetRelianceButtonStateForDetailedReport.showExpectedExcessAsset,
                includeLifeInsuranceAtDeath: assetRelianceButtonStateForDetailedReport.includeLifeInsuranceAtDeath,
                selectedFveDiscountRate: assetRelianceButtonStateForDetailedReport.selectedFveDiscountRate
            }
        }
        if (reportConfig.comparePlans !== null && reportConfig.comparePlans.isEnabled) {
            reportConfig.comparePlans.comparePlanOptions = {
                showDifferenceBetweenPlansForReport: comparePlanButtonStateForReport.showDifferenceBetweenPlansForReport,
            }
        }
        const advisorDocumentDetailsLocal: AdvisorDocumentMap = {};
        advisorDocumentDetailsLocal["AdviceLibrary"] = {
            advisorDocumentMap: {},
            menuTitle: "AdviceLibrary"
        }
        adviceLibrarySubMenu.advisorDocumentSubMenus.forEach(submenu => {

            let documentsList: string[] = [];
            if ((submenu.subMenuLabel !== 'GRAT' && submenu.subMenuLabel !== 'Gift to Grantor Trust' && submenu.subMenuLabel !== 'Annual Gifting' && submenu.subMenuLabel !== 'Change of Residency')) {
                submenu.subMenuItems.forEach(item => {
                    if (selectedAdviceLibraryDocuments.includes(item.documentLabel)) {
                        documentsList.push(item.documentLabel)
                    }
                });
            }

            if (documentsList.length !== 0) {
                advisorDocumentDetailsLocal["AdviceLibrary"].advisorDocumentMap[submenu.subMenuLabel] = documentsList;
            }
        });

        advisorDocumentDetailsLocal["PracticeDocuments"] = {
            advisorDocumentMap: {},
            menuTitle: "PracticeDocuments"
        }
        practiceDocumentSubMenu.advisorDocumentSubMenus.forEach(submenu => {
            let doclist: string[] = [];
            submenu.subMenuItems.forEach(item => {
                if (selectedPracticeDocuments.includes(item.documentLabel)) {
                    doclist.push(item.documentLabel)
                }
            });

            if (doclist.length !== 0) {
                advisorDocumentDetailsLocal["PracticeDocuments"].advisorDocumentMap[submenu.subMenuLabel] = doclist;
            }
        });

        const generateReportRequest: GenerateReportRequest = {
            pageUrl: `${App_Conf.APP_URL}/ReportPreview/${id}`,
            config: {
                ...reportConfig,
                coverPage: {
                    ...reportConfig.coverPage,
                    isEnabled: true
                },
                disclaimerPage: {
                    ...reportConfig.disclaimerPage,
                    isEnabled: true
                }
            },
            documentMap: advisorDocumentDetailsLocal,
            clientDocuments: selectedClientDocuments.map(clientDocument => {
                const extension = clientDocument.name.split('.').pop();
                return clientDocument.id + "." + extension;
            }),
            myDocuments: selectedMyDocuments.map(myDocument => {
                const extension = myDocument.name.split('.').pop();
                return myDocument.id + "." + extension;
            }),
            displayName: displayName
        };
        dispatch(setIsReportGenerationTriggered(true));
        reportingApiClient.generateReport(generateReportRequest, id).then(responseStatus => {
            setReportStatus(responseStatus.status);
        }).catch(resetReportStates);
    };

    const refreshReport = () => {
        setIsGenerating(true);
        dispatch(setIsReportGenerationTriggered(false));
    }

    if (!displayName) {
        return <LoadingIndicator/>
    }

    if (isGenerating) {
        return <LoadingIndicator className="loading-generate-report"
                                 displayMessage="Generating Report"
                                 displaySubText="Please wait as this might take a few minutes..."
        />
    }

    if (isUploading) {
        return <LoadingIndicator className="loading-generate-report"
                                 displayMessage="Uploading Report To Private Passport."
                                 displaySubText="Please wait as this might take a few seconds..."
        />
    }


    function onCloseSuccess() {
        if (onCloseSuccessMessage) {
            onCloseSuccessMessage()
        }
    }

    function onCloseFailure() {
        if (onCloseFailureMessage) {
            onCloseFailureMessage()
        }
    }

    return (
        <>
            {hasError ? <ReportingConnectionFailedPage onRefresh={refreshReport}/>
                :
                <div>
                    {(showMessageFromDCH || showFailureMessageFromWDS) && <div>
                        <DCHToasts showMessageFromDCH={showMessageFromDCH} messageFromDCH={messageFromDCH}
                                   onCloseSuccessMessage={onCloseSuccess} onCloseFailureMessage={onCloseFailure}
                                   showFailureMessageFromWDS={showFailureMessageFromWDS}
                                   failureMessageFromWDS={failureMessageFromWDS}/>
                    </div>}
                    <div className='preview-and-send' data-theme="print">
                        <Document
                            className="pdf-preview-pane__container"
                            file={generatedReportUrl}
                            error="PDF is missing"
                            onLoadSuccess={({numPages}) => {
                                setNoOfPages(numPages)
                            }}
                        >
                            {pagesList
                                .map((_page, index) =>
                                    <Page
                                        key={"pagenumber-"+index}
                                        pageNumber={index + 1}
                                        scale={120 / 60}
                                        onRenderSuccess={() => {
                                            if (index === noOfPages - 1) {
                                                dispatch(setHasCanvasRendered(true));
                                            }
                                        }}
                                    />
                                )
                            }
                        </Document></div>
                </div>
            }
        </>
    );
};


export default PreviewReport;
